import { NativeBridge } from "./native_bridge.js";
import type { CrashSnapshot } from "./types.js";

const STORAGE_KEY = 'bb_crash_snapshot_v1';
const MIRROR_MAX_INTERVAL_MS = 10_000;

export type SnapshotProvider = () => CrashSnapshot;

export class CrashStateSnapshot
{
  private static provider: SnapshotProvider | null = null;
  private static last: CrashSnapshot | null = null;
  private static last_serialized = '';
  private static last_mirrored_at = 0;
  private static previous: CrashSnapshot | null = null;
  private static frames = 0;
  private static frames_since = 0;

  public static init(): void
  {
    this.previous = this.read();
  }

  public static setProvider( provider: SnapshotProvider ): void
  {
    this.provider = provider;
  }

  public static countFrame(): void
  {
    this.frames++;
  }

  public static tick(): CrashSnapshot | null
  {
    const now = Date.now();
    const elapsed = this.frames_since > 0 ? now - this.frames_since : 0;
    const fps = elapsed > 0 ? Math.round( this.frames * 1000 / elapsed ) : null;
    this.frames = 0;
    this.frames_since = now;

    let base: CrashSnapshot = {};
    try { base = this.provider?.() ?? {}; }
    catch ( e ) { base = { provider_error: String( e ) }; }

    const snapshot: CrashSnapshot = { ...base, fps, canvases: countCanvases(), at: new Date().toISOString() };
    this.last = snapshot;
    const { at: _at, fps: _fps, uptime_ms: _uptime, foreground_ms: _foreground, ...stable } = snapshot as CrashSnapshot & Record<string, unknown>;
    const stable_serialized = JSON.stringify( stable );
    if ( stable_serialized !== this.last_serialized || now - this.last_mirrored_at >= MIRROR_MAX_INTERVAL_MS )
    {
      this.last_serialized = stable_serialized;
      this.last_mirrored_at = now;
      try { localStorage.setItem( STORAGE_KEY, JSON.stringify( snapshot ) ); }
      catch {}
      NativeBridge.snapshot( snapshot );
    }
    return snapshot;
  }

  public static current(): CrashSnapshot | null
  {
    return this.last;
  }

  public static previousRun(): CrashSnapshot | null
  {
    return this.previous;
  }

  private static read(): CrashSnapshot | null
  {
    try
    {
      const raw = localStorage.getItem( STORAGE_KEY );
      return raw ? JSON.parse( raw ) as CrashSnapshot : null;
    }
    catch
    {
      return null;
    }
  }
}

function countCanvases(): { n: number; mb: number }
{
  try
  {
    const list = document.querySelectorAll( 'canvas' );
    let bytes = 0;
    list.forEach( c => { bytes += c.width * c.height * 4; } );
    return { n: list.length, mb: Math.round( bytes / 1048576 ) };
  }
  catch
  {
    return { n: -1, mb: -1 };
  }
}
